import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { formatFixedNumber, minutesToHHMM, parseLegacyTimeToMinutes, toFiniteNumber } from '../../utils/slurry-material-calculation.helpers';

type SlurryInfoBlockedActionScope = 'infoHeader' | 'infoNotes';
type TargetThickeningTimeField = 'from' | 'to';

interface SlurryInfoBlockedActionClick {
  reason: string;
  scope: SlurryInfoBlockedActionScope;
}

@Component({
  selector: 'app-slurry-info-section',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule],
  templateUrl: './slurry-info-section.component.html'
})
export class SlurryInfoSectionComponent {
  @Input() targetThickeningTimeFrom = '';
  @Input() targetThickeningTimeTo = '';
  @Input() showMeasuredDepth = false;
  @Input() measuredDepth: number | string | null = null;
  @Input() slurryYield = 0;
  @Input() totalFluidPercent = 0;
  @Input() labVolumeMl: number | string = 0;
  @Input() slurryDensityPpg: number | string = 0;
  @Input() totalFluidGal = 0;
  @Input() equivalentSackLb = 0;
  @Input() notes1 = '';
  @Input() createFinalReportBlockedReason = '';
  @Input() appendClearNotesBlockedReason = '';
  @Input() blockedInfoHeaderActionMessage = '';
  @Input() blockedInfoNotesActionMessage = '';

  @Output() targetThickeningTimeFromChange = new EventEmitter<string>();
  @Output() targetThickeningTimeToChange = new EventEmitter<string>();
  @Output() measuredDepthChange = new EventEmitter<number | string | null>();
  @Output() labVolumeMlChange = new EventEmitter<number | string>();
  @Output() slurryDensityPpgChange = new EventEmitter<number | string>();
  @Output() notes1Change = new EventEmitter<string>();
  @Output() createFinalReportRequested = new EventEmitter<void>();
  @Output() appendClearNoteRequested = new EventEmitter<void>();
  @Output() recalculationRequested = new EventEmitter<void>();
  @Output() blockedActionClicked = new EventEmitter<SlurryInfoBlockedActionClick>();

  isSlurryDensityFocused = false;

  fmt(value: unknown, decimals: number): string {
    return formatFixedNumber(value, decimals);
  }

  onLabVolumeChanged(value: number | string): void {
    this.labVolumeMlChange.emit(value);
    this.recalculationRequested.emit();
  }

  onSlurryDensityChanged(value: number | string): void {
    this.slurryDensityPpgChange.emit(value);
    this.recalculationRequested.emit();
  }

  formatTargetThickeningTime(field: TargetThickeningTimeField): void {
    const current = field === 'from' ? this.targetThickeningTimeFrom : this.targetThickeningTimeTo;
    const minutes = parseLegacyTimeToMinutes(current);
    if (minutes == null) {
      return;
    }

    const formatted = minutesToHHMM(minutes);
    if (field === 'from') {
      this.targetThickeningTimeFromChange.emit(formatted);
    } else {
      this.targetThickeningTimeToChange.emit(formatted);
    }
  }

  formatSlurryDensityOnBlur(): void {
    this.isSlurryDensityFocused = false;
    const formatted = toFiniteNumber(this.slurryDensityPpg).toFixed(2);
    this.slurryDensityPpgChange.emit(formatted);
    this.recalculationRequested.emit();
  }

  onBlockedActionClick(reason: string, scope: SlurryInfoBlockedActionScope): void {
    this.blockedActionClicked.emit({ reason, scope });
  }
}
